import { useState } from "react";
import { useTranslation } from "react-i18next";
import Dropzone, { Accept } from "react-dropzone";
import { ExternalLink, RotateCcw, Trash2 } from "lucide-react";
import FileIcon from "@/assets/icons/file.svg?react";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getFile } from "@/api/s3.api";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { useFileTypeLabels } from "@/hooks/useEnumLabels";

export type ExistingFile = {
  fileId: string;
  fileName: string;
  fileUrl: string;
  fileType: FileTypeEnum;
};

export type PendingFile = {
  file: File;
  fileType: FileTypeEnum;
};

type FileDropzoneProps = {
  availableFileTypes: FileTypeEnum[];
  allowedFileExtensions: string[];
  currentFiles: ExistingFile[];
  maxFiles: number;
  pendingFiles: PendingFile[];
  filesToRemove: string[];
  onPendingFilesChange: (files: PendingFile[]) => void;
  onFilesToRemoveChange: (ids: string[]) => void;
  disabled?: boolean;
  error?: boolean;
};

const EXTENSION_TO_MIME: Record<string, string> = {
  ".pdf": "application/pdf",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xls": "application/vnd.ms-excel",
  ".csv": "text/csv",
  ".doc": "application/msword",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
};

function buildAccept(extensions: string[]): Accept {
  const accept: Accept = {};
  for (const ext of extensions) {
    const mime = EXTENSION_TO_MIME[ext.toLowerCase()];
    if (mime) {
      if (!accept[mime]) accept[mime] = [];
      accept[mime].push(ext);
    } else {
      if (!accept["application/octet-stream"]) accept["application/octet-stream"] = [];
      accept["application/octet-stream"].push(ext);
    }
  }
  return accept;
}

export const FileDropzone = ({
  availableFileTypes,
  allowedFileExtensions,
  currentFiles,
  maxFiles,
  pendingFiles,
  filesToRemove,
  onPendingFilesChange,
  onFilesToRemoveChange,
  disabled = false,
  error = false,
}: FileDropzoneProps) => {
  const { t } = useTranslation("shared");
  const fileTypeLabels = useFileTypeLabels();
  const [selectedFileType, setSelectedFileType] = useState<FileTypeEnum | null>(null);

  const visibleExistingFiles = currentFiles.filter((f) => !filesToRemove.includes(f.fileId));
  const removedExistingFiles = currentFiles.filter((f) => filesToRemove.includes(f.fileId));
  const totalFiles = visibleExistingFiles.length + pendingFiles.length;
  const isMaxReached = totalFiles >= maxFiles;

  const accept = buildAccept(allowedFileExtensions);

  const handleFileDrop = (acceptedFiles: File[]) => {
    if (!selectedFileType || acceptedFiles.length === 0) return;

    const remaining = maxFiles - totalFiles;
    const filesToAdd = acceptedFiles.slice(0, remaining);
    const newPending: PendingFile[] = filesToAdd.map((file) => ({
      file,
      fileType: selectedFileType,
    }));

    onPendingFilesChange([...pendingFiles, ...newPending]);
  };

  const handleRemovePending = (index: number) => {
    const updated = [...pendingFiles];
    updated.splice(index, 1);
    onPendingFilesChange(updated);
  };

  const handleRemoveExisting = (fileId: string) => {
    onFilesToRemoveChange([...filesToRemove, fileId]);
  };

  const handleRestoreExisting = (fileId: string) => {
    onFilesToRemoveChange(filesToRemove.filter((id) => id !== fileId));
  };

  const handleViewExisting = async (fileUrl: string) => {
    const downloadUrl = await getFile(fileUrl);
    window.open(downloadUrl, "_blank");
  };

  return (
    <div className="flex w-full flex-col gap-4">
      {visibleExistingFiles.map((file) => (
        <div
          key={file.fileId}
          className="flex w-full flex-row items-center justify-between gap-4 rounded-lg border bg-gray-50 p-4"
        >
          <div className="rounded-full border p-2">
            <FileIcon />
          </div>
          <div className="flex w-full flex-col justify-start gap-1 overflow-hidden">
            <p className="truncate text-sm font-medium text-gray-700">{file.fileName}</p>
            <Badge variant="lightGray">{fileTypeLabels[file.fileType]}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="rounded p-1 text-gray-600 hover:text-gray-800 focus:outline-hidden"
              onClick={() => handleViewExisting(file.fileUrl)}
              type="button"
              title={t("fileDropzone.viewFile")}
            >
              <ExternalLink className="size-4" />
            </button>
            <button
              className="rounded p-1 text-gray-600 hover:text-red-600 focus:outline-hidden"
              onClick={() => handleRemoveExisting(file.fileId)}
              type="button"
              title={t("fileDropzone.deleteFile")}
              disabled={disabled}
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        </div>
      ))}

      {removedExistingFiles.map((file) => (
        <div
          key={file.fileId}
          className="flex w-full flex-row items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 p-4 opacity-60"
        >
          <div className="rounded-full border p-2">
            <FileIcon />
          </div>
          <div className="flex w-full flex-col justify-start gap-1 overflow-hidden">
            <p className="truncate text-sm font-medium text-gray-400 line-through">
              {file.fileName}
            </p>
            <Badge variant="lightGray">{fileTypeLabels[file.fileType]}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="rounded p-1 text-gray-600 hover:text-green-600 focus:outline-hidden"
              onClick={() => handleRestoreExisting(file.fileId)}
              type="button"
              title={t("fileDropzone.restoreFile")}
              disabled={disabled}
            >
              <RotateCcw className="size-4" />
            </button>
          </div>
        </div>
      ))}

      {pendingFiles.map((pf, index) => (
        <div
          key={`pending-${index}`}
          className="flex w-full flex-row items-center justify-between gap-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4"
        >
          <div className="rounded-full border p-2">
            <FileIcon />
          </div>
          <div className="flex w-full flex-col justify-start gap-1 overflow-hidden">
            <p className="truncate text-sm font-medium text-gray-700">{pf.file.name}</p>
            <div className="flex items-center gap-2">
              <Badge variant="lightGray">{fileTypeLabels[pf.fileType]}</Badge>
              <span className="text-xs text-gray-500">
                {t("fileDropzone.fileSize", { size: Math.floor(pf.file.size / 1000) })}
              </span>
            </div>
          </div>
          <button
            className="rounded p-1 text-gray-600 hover:text-red-600 focus:outline-hidden"
            onClick={() => handleRemovePending(index)}
            type="button"
            title={t("fileDropzone.removeFile")}
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      ))}

      {!disabled && !isMaxReached && (
        <div className="flex flex-col gap-3">
          <Select
            value={selectedFileType ?? undefined}
            onValueChange={(value) => setSelectedFileType(value as FileTypeEnum)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("fileDropzone.fileTypePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {availableFileTypes.map((ft) => (
                <SelectItem key={ft} value={ft}>
                  {fileTypeLabels[ft]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedFileType ? (
            <Dropzone onDrop={handleFileDrop} accept={accept} maxFiles={maxFiles - totalFiles}>
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps()}
                  className={`${error ? "border-red-500" : "border-brand700"} bg-brand50 flex w-full items-center justify-center rounded-lg border-2 border-dashed p-4 hover:cursor-pointer`}
                >
                  <input {...getInputProps()} />
                  <span className="text-brand700 hover:text-brand600 truncate text-center text-sm font-medium text-wrap">
                    {t("fileDropzone.dropText")}
                  </span>
                </div>
              )}
            </Dropzone>
          ) : (
            <div className="flex w-full items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4">
              <span className="text-center text-sm text-gray-400">
                {t("fileDropzone.selectTypeFirst")}
              </span>
            </div>
          )}

          <span className="w-full text-center text-xs font-medium text-gray-500">
            {t("fileDropzone.acceptedFormats", {
              formats: allowedFileExtensions.join(", "),
              total: totalFiles,
              count: maxFiles,
            })}
          </span>
        </div>
      )}
    </div>
  );
};
